import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiRequestError } from "../api/client";
import { useCurrentCase } from "../hooks/useCurrentCase";
import { CREDIT_RATES } from "../../shared/constants";

// No.5 AIオプション確認。アドオン月額は廃止（DESIGN.md §2）。AI機能はクレジット消費のみ。
export function OptionsPage() {
  const { caseRow, loading } = useCurrentCase();
  const navigate = useNavigate();
  const [wantsChatbot, setWantsChatbot] = useState(true);
  const [wantsBlog, setWantsBlog] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const start = async () => {
    if (!caseRow) return;
    setError(null);
    setSubmitting(true);
    try {
      await api.post(`/api/cases/${caseRow.id}/generate`, {
        wants_chatbot: wantsChatbot,
        wants_blog: wantsBlog,
      });
      navigate("/generating");
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "生成の開始に失敗しました");
      setSubmitting(false);
    }
  };

  if (loading) return <div className="page-center">読み込み中...</div>;
  if (!caseRow) return <div className="page-center">案件が見つかりません。</div>;

  return (
    <section className="wizard">
      <div className="wizard-head">
        <span className="step-tag">No.5</span>
        <h1>機能オプション選択</h1>
        <p className="muted">
          サイトの目的に合わせて、AI機能を追加できます。AI機能は使った分だけのクレジット消費でご利用いただけます（月額の追加料金はありません）。
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="option-grid">
        <OptionCard
          title="AIチャットボット機能"
          recommended
          on={wantsChatbot}
          onToggle={() => setWantsChatbot((v) => !v)}
          desc="訪問者の質問に24時間自動で回答し、問い合わせ対応を効率化します。"
          benefits={["24時間365日の自動対応", "問い合わせ数の増加", "顧客満足度の向上", "よくある質問の負担軽減"]}
          rate={`応答1回あたり ${CREDIT_RATES.chatReply}cr`}
        />
        <OptionCard
          title="AIブログ機能"
          on={wantsBlog}
          onToggle={() => setWantsBlog((v) => !v)}
          desc="AIが記事を提案・生成し、SEOに強いコンテンツを継続的に配信できます。"
          benefits={["SEOに強い記事を自動生成", "専門性の高い情報発信", "継続的なアクセス増加", "集客力の強化"]}
          rate={`記事1本（1,000文字）あたり ${CREDIT_RATES.blogPer1000Chars}cr`}
        />
      </div>

      <div className="card credit-model">
        <h2>クレジットモデルについて</h2>
        <p className="muted">
          AI機能の利用には「クレジット」を消費します。使った分だけ消費される従量モデルです（1クレジット=1円・銀行振込でチャージ）。
        </p>
        <div className="credit-examples">
          <div>
            <span className="muted small">チャットボット回答（1回）</span>
            <strong>{CREDIT_RATES.chatReply}cr</strong>
          </div>
          <div>
            <span className="muted small">ブログ記事生成（1,000文字）</span>
            <strong>{CREDIT_RATES.blogPer1000Chars}cr</strong>
          </div>
          <div>
            <span className="muted small">AI画像生成（1枚）</span>
            <strong>{CREDIT_RATES.imageGeneration}cr</strong>
          </div>
          <div>
            <span className="muted small">ページ追加生成（1ページ）</span>
            <strong>{CREDIT_RATES.pageGeneration}cr</strong>
          </div>
          <div>
            <span className="muted small">デザイン再提案（6案）</span>
            <strong>{CREDIT_RATES.designReproposal}cr</strong>
          </div>
        </div>
        <p className="muted small">
          有効期限は付与から6ヶ月。オプションは公開後もいつでも追加・変更できます。
        </p>
      </div>

      <div className="cta-card card">
        <div>
          <h2>この内容でAIホームページの生成を開始します</h2>
          <p className="muted">
            AIがヒアリング内容をもとにトップページのデザイン6案を生成します（デザイン提案までは無料）。
          </p>
        </div>
        <button className="btn btn-primary btn-lg" onClick={start} disabled={submitting}>
          {submitting ? "生成を開始中..." : "この内容で生成を開始 →"}
        </button>
      </div>

      <div className="wizard-actions">
        <button className="btn btn-ghost" onClick={() => navigate("/profile-image")}>
          ← プロフィール画像に戻る
        </button>
      </div>
    </section>
  );
}

function OptionCard({
  title,
  desc,
  benefits,
  rate,
  recommended,
  on,
  onToggle,
}: {
  title: string;
  desc: string;
  benefits: string[];
  rate: string;
  recommended?: boolean;
  on: boolean;
  onToggle: () => void;
}) {
  return (
    <div className={`card option-card ${on ? "on" : ""}`}>
      <div className="option-card-head">
        <h3>
          {title}
          {recommended && <span className="tag tag-req">おすすめ</span>}
        </h3>
        <button
          type="button"
          className={`toggle ${on ? "on" : ""}`}
          onClick={onToggle}
          aria-pressed={on}
        >
          {on ? "ON" : "OFF"}
        </button>
      </div>
      <p className="muted">{desc}</p>
      <ul className="check-list">
        {benefits.map((b) => (
          <li key={b}>{b}</li>
        ))}
      </ul>
      <div className="option-rate">{rate}</div>
    </div>
  );
}
